import React, { createContext, useContext, useState, useCallback } from 'react';

interface CompanyInfo {
  name: string;
  website: string;
  address: string;
  phone: string;
  email: string;
  size: string;
}

interface CompanyInfoContextType {
  companyInfo: CompanyInfo;
  updateCompanyInfo: (newInfo: Partial<CompanyInfo>) => void;
  resetCompanyInfo: () => void;
}

const defaultCompanyInfo: CompanyInfo = {
  name: '',
  website: '',
  address: '',
  phone: '',
  email: '',
  size: '1-10'
};

const CompanyInfoContext = createContext<CompanyInfoContextType | undefined>(undefined);

export const useCompanyInfo = () => {
  const context = useContext(CompanyInfoContext);
  if (!context) {
    throw new Error('useCompanyInfo must be used within a CompanyInfoProvider');
  }
  return context;
};

export const CompanyInfoProvider: React.FC<{ children: React.ReactNode }> = ({ children }) => {
  const [companyInfo, setCompanyInfo] = useState<CompanyInfo>(defaultCompanyInfo);

  const updateCompanyInfo = useCallback((newInfo: Partial<CompanyInfo>) => {
    setCompanyInfo((prev) => ({ ...prev, ...newInfo }));
  }, []);

  const resetCompanyInfo = useCallback(() => {
    setCompanyInfo(defaultCompanyInfo);
  }, []);

  return (
    <CompanyInfoContext.Provider value={{ companyInfo, updateCompanyInfo, resetCompanyInfo }}>
      {children}
    </CompanyInfoContext.Provider>
  );
}; 